import React from "react";
import { useTranslation } from "react-i18next";
import {
  useEditSecondaryCriteriaTemplate,
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { UseCases } from "@planzone/core";
import { useMessage } from "@components/useMessage";
import { events } from "@planzone/events";
import {
  SecondaryCriteriaTemplate as Template,
  secondaryCriteria,
  SecondaryCriteriaElementType,
} from "@planzone/dto";
import { useModal } from "@root/client/modal";
import { Modal } from "@common/modal";
import { SecondaryCriteriaTemplatePlaceholder } from "@common/secondaryCriteriaTemplatePlaceholder";

import { Props } from "./types";
import { Criteria } from "./criteria";
import { ViewSelector } from "./viewSelector";
import { hasChanged } from "./utils";
import styles from "./styles.scss";

function SecondaryCriteriaTemplate({
  namespace,
  scope,
  elementType,
  close,
}: Props) {
  const { t } = useTranslation();
  const [isCreating, setIsCreating] = React.useState(false);
  const {
    isRequestPending: isProjectElementsRequestPending,
    isRequestSuccess: isProjectElementsRequestSuccess,
    isRequestFailure: isProjectElementsRequestFailure,
    template: projectElementsTemplate,
    retrieveSecondaryCriteriaTemplate:
      retrieveProjectElementsSecondaryCriteriaTemplate,
  } = useRetrieveSecondaryCriteriaTemplate(namespace, "projectElement");
  const {
    isRequestPending: isProjectsRequestPending,
    isRequestSuccess: isProjectsRequestSuccess,
    isRequestFailure: isProjectsRequestFailure,
    template: projectsTemplate,
    retrieveSecondaryCriteriaTemplate:
      retrieveProjectsSecondaryCriteriaTemplate,
  } = useRetrieveSecondaryCriteriaTemplate(namespace, "project");
  const {
    isRequestPending: isEditProjectElementsRequestPending,
    editSecondaryCriteriaTemplate: editProjectElementsSecondaryCriteriaTemplate,
  } = useEditSecondaryCriteriaTemplate(namespace, "projectElement");
  const {
    isRequestPending: isEditProjectsRequestPending,
    editSecondaryCriteriaTemplate: editProjectsSecondaryCriteriaTemplate,
  } = useEditSecondaryCriteriaTemplate(namespace, "project");
  const {
    isRequestPending: isResourcesRequestPending,
    isRequestSuccess: isResourcesRequestSuccess,
    isRequestFailure: isResourcesRequestFailure,
    template: resourcesTemplate,
    retrieveSecondaryCriteriaTemplate:
      retrieveResourcesSecondaryCriteriaTemplate,
  } = useRetrieveSecondaryCriteriaTemplate(namespace, "resource");
  const {
    isRequestPending: isEditResourcesRequestPending,
    editSecondaryCriteriaTemplate: editResourcesSecondaryCriteriaTemplate,
  } = useEditSecondaryCriteriaTemplate(namespace, "resource");
  const {
    isRequestPending: isProjectReportsRequestPending,
    isRequestSuccess: isProjectReportsRequestSuccess,
    isRequestFailure: isProjectReportsRequestFailure,
    template: projectReportsTemplate,
    retrieveSecondaryCriteriaTemplate:
      retrieveProjectReportsSecondaryCriteriaTemplate,
  } = useRetrieveSecondaryCriteriaTemplate(namespace, "projectReport");
  const {
    isRequestPending: isEditProjectReportsRequestPending,
    editSecondaryCriteriaTemplate: editProjectReportsSecondaryCriteriaTemplate,
  } = useEditSecondaryCriteriaTemplate(namespace, "projectReport");
  const {
    isRequestPending: isTimeRecordsRequestPending,
    isRequestSuccess: isTimeRecordsRequestSuccess,
    isRequestFailure: isTimeRecordsRequestFailure,
    template: timeRecordsTemplate,
    retrieveSecondaryCriteriaTemplate:
      retrieveTimeRecordsSecondaryCriteriaTemplate,
  } = useRetrieveSecondaryCriteriaTemplate(namespace, "timeRecord");
  const {
    isRequestPending: isEditTimeRecordsRequestPending,
    editSecondaryCriteriaTemplate: editTimeRecordsSecondaryCriteriaTemplate,
  } = useEditSecondaryCriteriaTemplate(namespace, "timeRecord");
  const { pushView } = useModal();
  const [currentView, setCurrentView] =
    React.useState<SecondaryCriteriaElementType>(
      elementType || "projectElement",
    );
  const [projectElementsFormData, setProjectElementsFormData] = React.useState(
    projectElementsTemplate,
  );
  const [projectsFormData, setProjectsFormData] =
    React.useState(projectsTemplate);
  const [resourcesFormData, setResourcesFormData] =
    React.useState(resourcesTemplate);
  const [projectReportsFormData, setProjectReportsFormData] = React.useState(
    projectReportsTemplate,
  );
  const [timeRecordsFormData, setTimeRecordsFormData] =
    React.useState(timeRecordsTemplate);
  const template =
    currentView === "project"
      ? projectsTemplate
      : currentView === "resource"
        ? resourcesTemplate
        : currentView === "projectReport"
          ? projectReportsTemplate
          : currentView === "timeRecord"
            ? timeRecordsTemplate
            : projectElementsTemplate;
  const formData =
    currentView === "project"
      ? projectsFormData
      : currentView === "resource"
        ? resourcesFormData
        : currentView === "projectReport"
          ? projectReportsFormData
          : currentView === "timeRecord"
            ? timeRecordsFormData
            : projectElementsFormData;
  const isEditRequestPending =
    currentView === "project"
      ? isEditProjectsRequestPending
      : currentView === "resource"
        ? isEditResourcesRequestPending
        : currentView === "projectReport"
          ? isEditProjectReportsRequestPending
          : currentView === "timeRecord"
            ? isEditTimeRecordsRequestPending
            : isEditProjectElementsRequestPending;
  const setFormData = (formData: Template) =>
    currentView === "project"
      ? setProjectsFormData(formData)
      : currentView === "resource"
        ? setResourcesFormData(formData)
        : currentView === "projectReport"
          ? setProjectReportsFormData(formData)
          : currentView === "timeRecord"
            ? setTimeRecordsFormData(formData)
            : setProjectElementsFormData(formData);
  const submit =
    currentView === "project"
      ? editProjectsSecondaryCriteriaTemplate
      : currentView === "resource"
        ? editResourcesSecondaryCriteriaTemplate
        : currentView === "projectReport"
          ? editProjectReportsSecondaryCriteriaTemplate
          : currentView === "timeRecord"
            ? editTimeRecordsSecondaryCriteriaTemplate
            : editProjectElementsSecondaryCriteriaTemplate;
  const [criteriaFormData, setCriteriaFormData] = React.useState({
    ...secondaryCriteria,
    hasChanged: true,
    uuid: "",
  });
  const addCriteria = () => {
    setIsCreating(true);
    setCriteriaFormData({
      ...secondaryCriteria,
      type: "text",
      uuid: "",
      hasChanged: true,
      accessRoles: {
        read: [
          "pz_admin",
          "pz_under_admin",
          "pz_user",
          "pz_visitor",
          "pr_manager",
          "pr_worker",
          "pr_visitor",
        ],
        write: [
          "pz_admin",
          "pz_under_admin",
          "pz_user",
          "pr_manager",
          "pr_worker",
        ],
      },
    });
  };

  useMessage(
    (event) =>
      event.type === events.editSecondaryCriteriaTemplateSucceeded &&
      setIsCreating(false),
  );

  React.useEffect(() => {
    setProjectElementsFormData(projectElementsTemplate);
  }, [projectElementsTemplate]);

  React.useEffect(() => {
    setProjectsFormData(projectsTemplate);
  }, [projectsTemplate]);

  React.useEffect(() => {
    setResourcesFormData(resourcesTemplate);
  }, [resourcesTemplate]);

  React.useEffect(() => {
    setProjectReportsFormData(projectReportsTemplate);
  }, [projectReportsTemplate]);

  React.useEffect(() => {
    setTimeRecordsFormData(timeRecordsTemplate);
  }, [timeRecordsTemplate]);

  React.useEffect(() => {
    (!elementType || elementType === "projectElement") &&
      !isProjectElementsRequestPending &&
      !isProjectElementsRequestSuccess &&
      !isProjectElementsRequestFailure &&
      retrieveProjectElementsSecondaryCriteriaTemplate({
        namespace,
        scope,
        elementType: "projectElement",
      });
  }, [namespace, scope]);

  React.useEffect(() => {
    (!elementType || elementType === "project") &&
      !isProjectsRequestPending &&
      !isProjectsRequestSuccess &&
      !isProjectsRequestFailure &&
      retrieveProjectsSecondaryCriteriaTemplate({
        namespace,
        scope,
        elementType: "project",
      });
  }, [namespace, scope]);

  React.useEffect(() => {
    (!elementType || elementType === "resource") &&
      !isResourcesRequestPending &&
      !isResourcesRequestSuccess &&
      !isResourcesRequestFailure &&
      retrieveResourcesSecondaryCriteriaTemplate({
        namespace,
        scope,
        elementType: "resource",
      });
  }, [namespace, scope]);

  React.useEffect(() => {
    (!elementType || elementType === "projectReport") &&
      !isProjectReportsRequestPending &&
      !isProjectReportsRequestSuccess &&
      !isProjectReportsRequestFailure &&
      retrieveProjectReportsSecondaryCriteriaTemplate({
        namespace,
        scope,
        elementType: "projectReport",
      });
  }, [namespace, scope]);

  React.useEffect(() => {
    scope === "planzone" &&
      (!elementType || elementType === "timeRecord") &&
      !isTimeRecordsRequestPending &&
      !isTimeRecordsRequestSuccess &&
      !isTimeRecordsRequestFailure &&
      retrieveTimeRecordsSecondaryCriteriaTemplate({
        namespace,
        scope,
        elementType: "timeRecord",
      });
  }, [namespace, scope]);

  const content = (
    <div className={styles.secondaryCriteriaTemplatePage}>
      {!elementType && (
        <div className={styles.toolbar}>
          <div className={styles.row}>
            <p className={styles.label}>
              {t("secondaryCriteriaTemplate.views.title")} :
            </p>
            <ViewSelector
              view={currentView}
              scope={scope}
              select={setCurrentView}
            />
          </div>
          <button className={styles.button} onClick={addCriteria}>
            <div className={styles.iconBox}>+</div>
            <p>{t("secondaryCriteriaTemplate.add")}</p>
          </button>
        </div>
      )}
      <div className={styles.list}>
        {isCreating && (
          <div className={styles.criteria}>
            <Criteria
              criteria={{
                ...criteriaFormData,
                originalRef: criteriaFormData,
                options: criteriaFormData.options.map((option) => ({
                  ...option,
                  originalRef: option,
                })),
              }}
              type={currentView}
              isRequestPending={isEditRequestPending}
              edit={(nextCriteria) =>
                setCriteriaFormData({ ...criteriaFormData, ...nextCriteria })
              }
              submit={(formData) =>
                submit({
                  namespace,
                  scope,
                  nextTemplate: [
                    {
                      ...formData,
                      options: formData.options.map((option) => ({
                        ...option,
                        accessRoles: formData.accessRoles,
                      })),
                      uuid: "",
                    },
                    ...template,
                  ],
                  elementType: currentView,
                })
              }
              cancel={() => setIsCreating(false)}
              isCreating
            />
          </div>
        )}
        {!isCreating && formData.length === 0 && (
          <div className={styles.placeholder}>
            <SecondaryCriteriaTemplatePlaceholder
              add={(data) => {
                setIsCreating(true);
                setCriteriaFormData(data);
              }}
            />
          </div>
        )}
        {formData.map((criteria, index) => {
          const originalRef = template.find((c) => c.uuid === criteria.uuid);

          return (
            <div key={criteria.uuid} className={styles.criteria}>
              <Criteria
                criteria={{
                  ...criteria,
                  originalRef: originalRef || criteria,
                  hasChanged: originalRef
                    ? hasChanged(originalRef, criteria)
                    : true,
                  options: criteria.options.map((option) => {
                    const originalOption = (
                      originalRef ? originalRef.options : []
                    ).find((o) => o.uuid === option.uuid);

                    return {
                      ...option,
                      originalRef: originalOption || option,
                      hasChanged: originalOption
                        ? hasChanged(originalOption, option)
                        : true,
                    };
                  }),
                }}
                type={currentView}
                edit={(nextCriteria) =>
                  setFormData([
                    ...formData.slice(0, index),
                    { ...criteria, ...nextCriteria },
                    ...formData.slice(index + 1),
                  ])
                }
                submit={(nextCriteria) =>
                  submit({
                    namespace,
                    scope,
                    nextTemplate: template.map((c) =>
                      c.uuid === criteria.uuid
                        ? {
                            ...c,
                            ...nextCriteria,
                            options: nextCriteria.options.map((o) => ({
                              ...o,
                              accessRoles: nextCriteria.accessRoles,
                            })),
                          }
                        : c,
                    ),
                    elementType: currentView,
                  })
                }
                reset={() =>
                  setFormData([
                    ...formData.slice(0, index),
                    template[index] || criteria,
                    ...formData.slice(index + 1),
                  ])
                }
                remove={(criteria) =>
                  pushView({
                    key: UseCases.editSecondaryCriteriaTemplate,
                    data: {
                      scope,
                      namespace,
                      isDangerous: true,
                      elementType: currentView,
                      nextTemplate: template
                        .filter((c) => c.uuid !== criteria.uuid)
                        .map((c) => ({
                          ...c,
                          options: c.options
                            .filter((o) => o.uuid !== criteria.uuid)
                            .map((o) => ({
                              ...o,
                              accessRoles: c.accessRoles,
                            })),
                        })),
                    },
                  })
                }
                isRequestPending={isEditRequestPending}
              />
            </div>
          );
        })}
      </div>
      {!!elementType && (
        <div className={styles.footer}>
          <button className={styles.button} onClick={addCriteria}>
            <div className={styles.iconBox}>+</div>
            <p>{t("secondaryCriteriaTemplate.add")}</p>
          </button>
        </div>
      )}
    </div>
  );

  if (!close) return content;

  return (
    <Modal
      header={t("secondaryCriteriaTemplate.title")}
      className={styles.modal}
      close={close}
    >
      {content}
    </Modal>
  );
}

export { SecondaryCriteriaTemplate };
